import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Material } from '../types';
import { formatCurrency } from '../utils/units';
import { 
  Star, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Info, 
  ArrowRight, 
  SunMoon, 
  Check,
  Search,
  Heart,
  Sparkles,
  Truck
} from 'lucide-react';

interface MaterialDialogProps {
  show: boolean;
  onClose: () => void;
  onSelect: (material: Material) => void;
  materials: Material[];
  selectedMaterial: Material | null;
  selectedMaterials?: Set<string>;
  currency?: string;
  language?: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

const dialogVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const cardVariants = {
  initial: { 
    scale: 1,
    borderColor: 'rgba(180, 160, 108, 0.2)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  },
  hover: { 
    scale: 1.02,
    borderColor: 'rgba(180, 160, 108, 1)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  },
  tap: { 
    scale: 0.98,
    borderColor: 'rgba(139, 115, 85, 1)',
    transition: {
      duration: 0.1,
      ease: 'easeOut'
    }
  }
};

const glowVariants = {
  initial: {
    opacity: 0,
    scale: 0.95
  },
  hover: {
    opacity: [0.4, 0.2, 0.4],
    scale: 1.05,
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear"
    }
  }
};

export const MaterialDialog: React.FC<MaterialDialogProps> = ({
  show,
  onClose,
  onSelect,
  materials,
  selectedMaterial,
  selectedMaterials = new Set(),
  currency = 'EUR',
  language = 'en-US'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState<string | null>(null);

  // Reset search and filters when dialog opens
  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setSelectedCategory(null);
      setShowDetails(null);
    }
  }, [show]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  const categories = [...new Set(materials.map(m => m.category))];

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || material.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            variants={dialogVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative w-[calc(100%-2rem)] sm:w-[calc(100%-4rem)] max-w-6xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] bg-cream rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 sticky top-0 z-10 bg-cream border-b border-gold/10 px-4 sm:px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-gold-dark">
                  Select Material
                </h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-gold/5 rounded-full text-gold hover:text-gold-dark transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </motion.button>
              </div>

              {/* Search and Categories */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/60 w-5 h-5" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search materials..."
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gold/20 rounded-xl focus:ring-2 focus:ring-gold focus:border-transparent placeholder-gold/60 text-black"
                  />
                </div>

                {/* Categories */}
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <motion.button
                      key={category}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                      className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-gold text-white'
                          : 'bg-white text-gold hover:bg-gold/5'
                      }`}
                    >
                      {category}
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>

            {/* Material List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredMaterials.map(material => {
                  const isSelected = selectedMaterials.has(material.id);
                  
                  return (
                    <motion.div
                      key={material.id}
                      variants={cardVariants}
                      initial="initial"
                      whileHover="hover"
                      whileTap="tap"
                      onClick={() => onSelect(material)}
                      className={`relative bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                        isSelected ? 'border-gold' : 'border-gold/20 hover:border-gold/40'
                      }`}
                    >
                      <div className="relative h-40">
                        <img
                          src={material.image}
                          alt={material.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold mb-1 line-clamp-1">{material.name}</h3>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < material.popularity / 20
                                      ? 'text-gold fill-current'
                                      : 'text-white/30'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-white/80 text-sm">
                              {material.popularity}% positive
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        <p className="text-black/70 text-sm mb-4 line-clamp-2">
                          {material.description}
                        </p>

                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-black/60">Price per m²</p>
                            <p className="text-xl font-bold text-gold">
                              {formatCurrency(material.price, currency, language)}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDetails(showDetails === material.id ? null : material.id);
                              }}
                              className="p-2 hover:bg-gold/5 rounded-lg text-gold transition-colors"
                            >
                              <Info className="w-5 h-5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                                isSelected
                                  ? 'bg-gold/10 text-gold'
                                  : 'bg-gold text-white hover:bg-gold-dark'
                              }`}
                            >
                              {isSelected ? (
                                <>
                                  Selected
                                  <Check className="w-4 h-4" />
                                </>
                              ) : (
                                <>
                                  Select
                                  <ArrowRight className="w-4 h-4" />
                                </>
                              )}
                            </motion.button>
                          </div>
                        </div>

                        {/* Material Details */}
                        <AnimatePresence>
                          {showDetails === material.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-4 pt-4 border-t border-gold/10"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-gold" />
                                    <span className="text-sm text-black/70">
                                      10-year warranty
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Heart className="w-4 h-4 text-gold" />
                                    <span className="text-sm text-black/70">
                                      {material.popularity}% satisfaction
                                    </span>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-gold" />
                                    <span className="text-sm text-black/70">
                                      Premium grade
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Truck className="w-4 h-4 text-gold" />
                                    <span className="text-sm text-black/70">
                                      {material.stock} m² in stock
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gold-dark mb-2">
                                  Available Finishes
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {material.finish.map(finish => (
                                    <span
                                      key={finish}
                                      className="px-3 py-1 bg-gold/10 text-gold rounded-full text-sm"
                                    >
                                      {finish}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              
              {filteredMaterials.length === 0 && (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-gold/40 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gold-dark mb-2">No materials found</h3>
                  <p className="text-black/60">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="border-t border-gold/10 p-4 sm:p-6 flex justify-between items-center bg-cream">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory(null);
                }}
                className="text-gold hover:text-gold-dark transition-colors"
              >
                Clear filters
              </button>
              
              <button
                onClick={onClose}
                className="px-6 py-2 bg-white border border-gold text-gold rounded-lg hover:bg-gold/5 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};